import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map, of, pipe, switchMap, take, tap } from 'rxjs';

import { AlertingService } from '@core/api/alerting/alerting.service';
import { ChargerDetailsService } from '@core/api/charger-details/charger-details.service';
import { SingleSiteService } from '@core/api/single-site/single-site.service';
import { BreakpointService } from '@core/services/breakpoint.service';
import { ToastService } from '@core/services/toast.service';

import {
  mapAlertWebsocketMessageApiModelToStoreModel,
  mapAlertsRequestStoreModelToApiModel,
  mapAlertsResponseApiModelToStoreModel,
} from '@domain/alerting/alerting-models.mappings';
import { AlertStateStoreModelEnum } from '@domain/alerting/alerting.enums';
import { AlertDtoStoreModel, AlertsRequestStoreModel } from '@domain/alerting/alerting.models';
import {
  mapChargingTimelineResponseApiModelToStoreModel,
  mapChargingTimelineWebsocketMessageApiModelToStoreModel,
} from '@domain/charger-details/charger-details-models.mappings';
import { ChargingTimelineResponseStoreModel } from '@domain/charger-details/charger-details.models';
import {
  mapEvseDetailsResponseApiModelToStoreModel,
  mapEvseDetailsWebsocketMessageApiModelToStoreModel,
} from '@domain/single-site/single-site-models.mappings';
import { SiteTypeStoreModelEnum } from '@domain/single-site/single-site.enums';
import { EvseDetailsResponseStoreModel } from '@domain/single-site/single-site.models';

import { ToastMessageType } from '@shared/components/toast-message/toast-message.view-model';
import { withApiResource } from '@shared/stores/features/with-api-resource.feature';
import { withCancellation } from '@shared/stores/features/with-cancellation.feature';
import { withReloadOnReconnect } from '@shared/stores/features/with-reload-on-reconnect.feature';
import { withResourceWebsocket } from '@shared/stores/features/with-resource-websocket.feature';

import { createActiveAlertsRequest } from './helpers/create-active-alerts-request.helper';
import { mergeEvseTimelineStoreModels } from './mappings';

export interface AssetDetailsDialogContext {
  siteId: string;
  siteType: SiteTypeStoreModelEnum;
  evseId: string;
}

interface AssetDetailsDialogStoreState {
  context: AssetDetailsDialogContext | undefined;
  evseDetails: EvseDetailsResponseStoreModel | undefined;
  activeAlerts: AlertDtoStoreModel[];
  activityTimeline: ChargingTimelineResponseStoreModel | undefined;
  timelineCollapsed: boolean;
  manualPriorityIsLoading: boolean;
}

const initialState: AssetDetailsDialogStoreState = {
  context: undefined,
  evseDetails: undefined,
  activeAlerts: [],
  activityTimeline: undefined,
  timelineCollapsed: false,
  manualPriorityIsLoading: false,
};

export const AssetDetailsDialogStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCancellation('clear'),
  withFeature((store) => {
    const singleSiteService = inject(SingleSiteService);
    return withApiResource(
      'evseDetails',
      (context: AssetDetailsDialogContext) => singleSiteService.getMapDetails$(context.siteId, context.evseId),
      store.clear$
    );
  }),
  withFeature((store) => {
    const alertingService = inject(AlertingService);
    return withApiResource(
      'activeAlerts',
      (request: AlertsRequestStoreModel) => alertingService.getAlerts$(mapAlertsRequestStoreModelToApiModel(request)),
      store.clear$
    );
  }),
  withFeature((store) => {
    const chargerDetailsService = inject(ChargerDetailsService);
    return withApiResource(
      'activityTimeline',
      ({ evseId, beforeTime }: { evseId: string; beforeTime?: string }) =>
        chargerDetailsService.getEvseActivityTimeline$(evseId, 20, beforeTime),
      store.clear$
    );
  }),
  withFeature((store) => {
    const alertingService = inject(AlertingService);
    return withResourceWebsocket(
      'activeAlerts',
      store.activeAlertsLoadStart$,
      store.activeAlertsLoadSuccess$,
      (request: AlertsRequestStoreModel) => alertingService.getAlertsWebsocketMessage$(mapAlertsRequestStoreModelToApiModel(request)),
      store.clear$
    );
  }),
  withFeature((store) => {
    const singleSiteService = inject(SingleSiteService);
    return withResourceWebsocket(
      'evseDetails',
      store.evseDetailsLoadStart$,
      store.evseDetailsLoadSuccess$,
      (context: AssetDetailsDialogContext) => singleSiteService.getMapDetailsWebsocketMessage$(context.siteId, context.evseId),
      store.clear$
    );
  }),
  withFeature((store) => {
    const chargerDetailsService = inject(ChargerDetailsService);
    return withResourceWebsocket(
      'activityTimeline',
      store.activityTimelineLoadStart$,
      store.activityTimelineLoadSuccess$,
      ({ evseId }: { evseId: string }) => chargerDetailsService.getEvseActivityTimelineWebsocketMessage$(evseId),
      store.clear$
    );
  }),
  withFeature((store) => withReloadOnReconnect('evseDetails', store.evseDetailsLoadStart$, store.evseDetailsLoad, store.clear$)),
  withComputed(
    ({
      context,
      evseDetails,
      activeAlerts,
      evseDetailsIsLoading,
      activityTimeline,
      activityTimelineIsLoading,
      manualPriorityIsLoading,
    }) => ({
      isLoading: computed(() => evseDetailsIsLoading()),
      hasContext: computed(() => context() !== undefined),
      details: computed(() => evseDetails()),
      alerts: computed(() => activeAlerts()),
      timeline: computed(() => activityTimeline()),
      timelineIsLoading: computed(() => activityTimelineIsLoading()),
      manualPriorityLoading: computed(() => manualPriorityIsLoading()),
    })
  ),
  withMethods((store) => {
    const breakpointService = inject(BreakpointService);
    const chargerDetailsService = inject(ChargerDetailsService);
    const toastService = inject(ToastService);
    const translateService = inject(TranslateService);

    const applyEvseDetails = rxMethod<ReturnType<typeof mapEvseDetailsResponseApiModelToStoreModel>>(
      pipe(
        tap((evseDetails) => {
          patchState(store, { evseDetails });
          store.activeAlertsLoad(createActiveAlertsRequest(evseDetails));
        })
      )
    );
    applyEvseDetails(store.evseDetailsLoadSuccess$.pipe(map(mapEvseDetailsResponseApiModelToStoreModel)));

    const applyEvseDetailsMessage = rxMethod<ReturnType<typeof mapEvseDetailsWebsocketMessageApiModelToStoreModel>>(
      pipe(
        tap((message) => {
          const evseDetails = store.evseDetails();
          if (evseDetails) {
            const updatedEvseDetails = {
              ...evseDetails,
              timeline: message.timeline ? mergeEvseTimelineStoreModels(message.timeline, evseDetails.timeline) : evseDetails.timeline,
              ...message,
            };
            patchState(store, {
              evseDetails: updatedEvseDetails,
            });
            if (
              evseDetails.evseId !== updatedEvseDetails.evseId ||
              evseDetails.chargerId !== updatedEvseDetails.chargerId ||
              evseDetails.vehicleUUID !== updatedEvseDetails.vehicleUUID
            ) {
              patchState(store, { activeAlerts: [] });
              store.activeAlertsLoad(createActiveAlertsRequest(updatedEvseDetails));
            }
          }
        })
      )
    );
    applyEvseDetailsMessage(store.evseDetailsMessage$.pipe(map(mapEvseDetailsWebsocketMessageApiModelToStoreModel)));

    const applyActiveAlerts = rxMethod<ReturnType<typeof mapAlertsResponseApiModelToStoreModel>>(
      pipe(
        tap(({ alerts }) => patchState(store, { activeAlerts: alerts.filter((alert) => alert.state === AlertStateStoreModelEnum.Firing) }))
      )
    );
    applyActiveAlerts(store.activeAlertsLoadSuccess$.pipe(map(mapAlertsResponseApiModelToStoreModel)));

    const applyActiveAlertMessage = rxMethod<ReturnType<typeof mapAlertWebsocketMessageApiModelToStoreModel>>(
      pipe(
        tap(({ alert }) => {
          const activeAlerts = store.activeAlerts();
          patchState(store, {
            activeAlerts:
              alert.state === AlertStateStoreModelEnum.Firing
                ? [...activeAlerts.filter(({ id }) => id !== alert.id), alert]
                : activeAlerts.filter(({ id }) => id !== alert.id),
          });
        })
      )
    );
    applyActiveAlertMessage(store.activeAlertsMessage$.pipe(map(mapAlertWebsocketMessageApiModelToStoreModel)));

    const applyTimeline = rxMethod<{ timeline: ChargingTimelineResponseStoreModel; beforeTime?: string }>(
      pipe(
        tap(({ timeline, beforeTime }) => {
          const currentTimeline = store.activityTimeline();
          patchState(store, {
            activityTimeline:
              beforeTime && currentTimeline ? { ...timeline, events: [...currentTimeline.events, ...timeline.events] } : timeline,
          });
        })
      )
    );
    applyTimeline(
      store.activityTimelineLoadStart$.pipe(
        switchMap(({ beforeTime }) =>
          store.activityTimelineLoadSuccess$.pipe(
            take(1),
            map(mapChargingTimelineResponseApiModelToStoreModel),
            map((timeline) => ({ timeline, beforeTime }))
          )
        )
      )
    );

    const applyTimelineMessage = rxMethod<ReturnType<typeof mapChargingTimelineWebsocketMessageApiModelToStoreModel>>(
      pipe(
        tap((message) => {
          const activityTimeline = store.activityTimeline();
          if (activityTimeline) {
            patchState(store, { activityTimeline: { ...activityTimeline, events: [message.event, ...activityTimeline.events] } });
          }
        })
      )
    );
    applyTimelineMessage(store.activityTimelineMessage$.pipe(map(mapChargingTimelineWebsocketMessageApiModelToStoreModel)));

    const updateManualPriority = rxMethod<{ evseId: string; manuallyPrioritized: boolean }>(
      pipe(
        tap(() => patchState(store, { manualPriorityIsLoading: true })),
        switchMap(({ evseId, manuallyPrioritized }) =>
          chargerDetailsService.setChargingSessionPriority$(evseId, { manuallyPrioritized }).pipe(
            tapResponse({
              next: (response) => {
                const evseDetails = store.evseDetails();
                patchState(store, {
                  manualPriorityIsLoading: false,
                  evseDetails: evseDetails && { ...evseDetails, manuallyPrioritized: response.manuallyPrioritized },
                });
                toastService.openToast({
                  type: ToastMessageType.Success,
                  message$: of(
                    translateService.instant(
                      response.manuallyPrioritized
                        ? 'single-site.map-asset-details-dialog.prioritize-session-success'
                        : 'single-site.map-asset-details-dialog.clear-manual-priority-success'
                    )
                  ),
                  closeable: true,
                  duration: 7000,
                });
              },
              error: () => {
                patchState(store, { manualPriorityIsLoading: false });
                toastService.openToast({
                  type: ToastMessageType.Error,
                  message$: of(translateService.instant('single-site.map-asset-details-dialog.set-charging-session-priority-failed')),
                  closeable: true,
                  duration: 7000,
                });
              },
            })
          )
        )
      )
    );

    let listenersStarted = false;

    const open = (context: AssetDetailsDialogContext): void => {
      store.clear();
      patchState(store, { ...initialState, context });
      if (!listenersStarted) {
        listenersStarted = true;
        store.evseDetailsStartWebsocket();
        store.evseDetailsReloadOnReconnect();
        store.activityTimelineStartWebsocket();
        store.activeAlertsStartWebsocket();
      }
      store.evseDetailsLoad(context);
      if (breakpointService.lg) {
        store.activityTimelineLoad({ evseId: context.evseId });
      }
    };

    return {
      open,
      selectRelatedEvse(evseId: string): void {
        const context = store.context();
        if (context) {
          open({ ...context, evseId });
        }
      },
      close(): void {
        store.clear();
        patchState(store, initialState);
      },
      setTimelineCollapsed(timelineCollapsed: boolean): void {
        patchState(store, { timelineCollapsed });
      },
      loadMoreTimeline(): void {
        const context = store.context();
        const timeline = store.activityTimeline();
        if (context && timeline) {
          store.activityTimelineLoad({
            evseId: context.evseId,
            beforeTime: timeline.events[timeline.events.length - 1]?.timestamp,
          });
        }
      },
      setManualPriority(evseId: string, manuallyPrioritized: boolean): void {
        updateManualPriority({ evseId, manuallyPrioritized });
      },
    };
  })
);
